import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Container, SectionHeading, Btn } from '../components/ui';
import PageHero from '../components/PageHero';
import IntelligenceHero from '../components/IntelligenceHero';
import Reveal from '../components/Reveal';
import Icon from '../components/Icon';
import CTASection from '../components/CTASection';
import Seo from '../components/Seo';
import { breadcrumbJsonLd, faqJsonLd } from '../data/seo';
import { FeatureCard, CheckList, CaseStudyCard } from '../components/cards';
import FAQAccordion from '../components/FAQAccordion';
import VcrCalculator from '../components/VcrCalculator';
import { SOLUTIONS, getCaseStudy, tryExtractionUrl } from '../data/content';

export default function SolutionDetail() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const seg = SOLUTIONS[slug];
  if (!seg) return <Navigate to="/solutions" replace />;
  const cs = getCaseStudy(seg.caseStudy);

  return (
    <div>
      <Seo
        title={`${seg.name} — Utility Bill Management`}
        description={seg.intro || seg.headline}
        keywords={[seg.name, 'utility bill management', 'expense management', 'BillPortal']}
        path={`/solutions/${seg.slug}`}
        jsonLd={[
          breadcrumbJsonLd([{ name: 'Solutions', path: '/solutions' }, { name: seg.name, path: `/solutions/${seg.slug}` }]),
          faqJsonLd(seg.faqs || seg.faq),
        ].filter(Boolean)}
      />
      {seg.intelligence ? (
        <IntelligenceHero
          seg={seg}
          actions={
            <>
              <Btn to="/demo" size="lg" arrow>{t('common.requestDemo')}</Btn>
              <Btn href={tryExtractionUrl('solution_' + seg.slug)} size="lg" variant="outline" className="border-white/25 text-white hover:bg-white/10">{t('hero.ctaTryParser')}</Btn>
            </>
          }
        />
      ) : (
        <PageHero tone="dark" align="left" image={seg.image} eyebrow={seg.name} title={seg.headline} subtitle={seg.intro}>
          <Btn to="/demo" size="lg" arrow>{t('common.requestDemo')}</Btn>
          <Btn to="/platform" size="lg" variant="outline" className="border-white/25 text-white hover:bg-white/10">{t('nav.platform')}</Btn>
        </PageHero>
      )}

      {/* Problems (hidden on intelligence-style pages to cut redundancy) */}
      {!seg.intelligence && (
      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <Container>
          <Reveal><SectionHeading align="left" eyebrow="The challenge" title="What gets in the way today" /></Reveal>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {seg.problems.map((p, i) => (
              <Reveal key={p} delay={i * 60}>
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-white/[0.03]">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand"><Icon name="AlertTriangle" className="h-4 w-4" /></span>
                  <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
      )}

      {/* Capabilities */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="How BillPortal helps" title={`Built for ${seg.name.toLowerCase()}`} /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {seg.capabilities.map((c, i) => (<Reveal key={c.title} delay={i * 60}><FeatureCard {...c} /></Reveal>))}
          </div>
        </Container>
      </section>

      {/* VCR ROI calculator (multifamily only) */}
      {seg.slug === 'multifamily' && <VcrCalculator />}

      {/* Outcomes + case study */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <SectionHeading align="left" eyebrow="Outcomes" title="What good looks like" />
              <CheckList className="mt-7" items={seg.outcomes} />
            </Reveal>
            {cs && (
              <Reveal delay={80}>
                <CaseStudyCard cs={cs} />
              </Reveal>
            )}
          </div>
        </Container>
      </section>

      {seg.narrative && !seg.intelligence && (
        <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
          <Container>
            <Reveal><SectionHeading align="left" eyebrow="In depth" title={`Why ${seg.name.toLowerCase()} teams take control of utilities`} /></Reveal>
            <div className="mt-10 max-w-3xl space-y-10">
              {seg.narrative.map((n, i) => (
                <Reveal key={n.title} delay={i * 60}>
                  <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white">{n.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-slate-600 dark:text-slate-300">{n.text}</p>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {seg.faq && (
        <section className="px-5 py-16 sm:px-8 sm:py-20">
          <Container>
            <Reveal><SectionHeading eyebrow="FAQ" title={`${seg.name} — your questions answered`} /></Reveal>
            <Reveal delay={80} className="mt-10"><FAQAccordion items={seg.faq} /></Reveal>
          </Container>
        </section>
      )}

      <CTASection />
    </div>
  );
}
